import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService, { EmailCampaign, EmailMailbox, EmailTemplate, EmailPlaybookMeta } from '../services/emailService';
import { toast } from 'react-hot-toast';
import MultiStepCampaignModal from '../components/email/campaigns/MultiStepCampaignModal';
import AICampaignDesignerModal from '../components/email/campaigns/AICampaignDesignerModal';
import PlaybookSelectorModal from '../components/email/campaigns/PlaybookSelectorModal';
import GuidedLaunchModal from '../components/email/campaigns/GuidedLaunchModal';
import CampaignCard from '../components/email/ui/CampaignCard';
import EmptyState from '../components/email/ui/EmptyState';
import StatCard from '../components/email/ui/StatCard';
import { Mail, Sparkles, Search, Filter, MailOpen, MessageCircle, AlertCircle } from 'lucide-react';

const ModernEmailCampaigns: React.FC = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<EmailCampaign[]>([]);
  const [mailboxes, setMailboxes] = useState<EmailMailbox[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCampaign, setEditCampaign] = useState<EmailCampaign | null>(null);
  const [initialCampaignDraft, setInitialCampaignDraft] = useState<Partial<EmailCampaign> | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(false);
  const [playbooks, setPlaybooks] = useState<EmailPlaybookMeta[]>([]);
  const [showDesignerModal, setShowDesignerModal] = useState(false);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [showGuidedLaunch, setShowGuidedLaunch] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchMailboxes();
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, searchQuery, statusFilter]);

  const filterCampaigns = () => {
    let filtered = [...campaigns];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.subject?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    setFilteredCampaigns(filtered);
  };

  const fetchCampaigns = async () => {
    try {
      const data = await emailService.getCampaigns();
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMailboxes = async () => {
    try {
      const data = await emailService.getMailboxes();
      setMailboxes(data || []);
    } catch (error) {
      console.error('Error fetching mailboxes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await emailService.getTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreateCampaign = async (campaignData: EmailCampaign) => {
    setIsCreating(true);
    try {
      if (editCampaign && editCampaign.id) {
        await emailService.updateCampaign({ ...campaignData, id: editCampaign.id });
        toast.success('Campaign updated successfully');
      } else {
        await emailService.createCampaign(campaignData);
        toast.success('Campaign created successfully');
      }
      setShowCreateModal(false);
      setEditCampaign(null);
      fetchCampaigns();
    } catch (error) {
      console.error('Error with campaign:', error);
      toast.error(`Failed to ${editCampaign ? 'update' : 'create'} campaign`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleCampaignStatus = async (campaign: EmailCampaign) => {
    const currentStatus = campaign.status || 'paused';
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';

    try {
      if (campaign.id) {
        await emailService.updateCampaignStatus(campaign.id, newStatus);
        setCampaigns(campaigns.map(c =>
          c.id === campaign.id ? { ...c, status: newStatus } : c
        ));
        toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      }
    } catch (error) {
      console.error('Error updating campaign status:', error);
      toast.error('Failed to update campaign status');
    }
  };

  const handleDeleteCampaign = async (campaign: EmailCampaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;

    try {
      if (campaign.id) {
        await emailService.deleteCampaign(campaign.id);
        setCampaigns(campaigns.filter(c => c.id !== campaign.id));
        toast.success('Campaign deleted');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleEditCampaign = (campaign: EmailCampaign) => {
    setEditCampaign(campaign);
    setInitialCampaignDraft(undefined);
    setShowCreateModal(true);
  };

  const handleStartFromPlaybook = () => {
    setEditCampaign(null);
    setInitialCampaignDraft(undefined);
    setShowPlaybookModal(true);
  };

  const handleAICampaignDesign = async () => {
    setEditCampaign(null);
    setInitialCampaignDraft(undefined);
    setShowDesignerModal(true);
  };

  // Calculate stats
  const totalSent = campaigns.reduce((sum, c) => sum + (c.sent_count || 0), 0);
  const totalOpened = campaigns.reduce((sum, c) => sum + (c.opened_count || 0), 0);
  const totalReplied = campaigns.reduce((sum, c) => sum + (c.replied_count || 0), 0);
  const totalBounced = campaigns.reduce((sum, c) => sum + (c.bounced_count || 0), 0);
  const avgOpenRate = totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0.0';
  const avgReplyRate = totalSent > 0 ? ((totalReplied / totalSent) * 100).toFixed(1) : '0.0';
  const bounceRate = totalSent > 0 ? ((totalBounced / totalSent) * 100).toFixed(1) : '0.0';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Mail}
          label="Total Sent"
          value={totalSent.toLocaleString()}
          change="+12%"
          trend="up"
          color="blue"
        />
        <StatCard
          icon={MailOpen}
          label="Open Rate"
          value={`${avgOpenRate}%`}
          change="+5.2%"
          trend="up"
          color="green"
        />
        <StatCard
          icon={MessageCircle}
          label="Reply Rate"
          value={`${avgReplyRate}%`}
          change="+3.1%"
          trend="up"
          color="purple"
        />
        <StatCard
          icon={AlertCircle}
          label="Bounce Rate"
          value={`${bounceRate}%`}
          change="-1.2%"
          trend="down"
          color="red"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search campaigns..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Results Count + Actions */}
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>
              {filteredCampaigns.length} of {campaigns.length} campaigns
            </span>
            <button
              type="button"
              onClick={() => {
                setEditCampaign(null);
                setInitialCampaignDraft(undefined);
                setShowGuidedLaunch(true);
              }}
              className="text-xs px-3 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              disabled={isCreating}
            >
              Guided launch (beginner)
            </button>
            <button
              type="button"
              onClick={handleStartFromPlaybook}
              className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
              disabled={isCreating}
            >
              {isLoadingPlaybooks ? 'Loading playbooks…' : 'Start from Playbook'}
            </button>
            <button
              type="button"
              onClick={handleAICampaignDesign}
              className="text-xs px-3 py-1 rounded-full border border-purple-200 text-purple-700 hover:bg-purple-50"
              disabled={isCreating}
            >
              Design with AI
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      {filteredCampaigns.length === 0 ? (
        <EmptyState
          icon={Mail}
          title={searchQuery || statusFilter !== 'all' ? 'No campaigns found' : 'No campaigns yet'}
          description={
            searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters to see more campaigns'
              : 'Create your first email campaign to start reaching prospects'
          }
          primaryAction={
            searchQuery || statusFilter !== 'all'
              ? undefined
              : {
                  label: 'Create Campaign',
                  icon: Sparkles,
                  onClick: () => setShowCreateModal(true)
                }
          }
          secondaryActions={
            searchQuery || statusFilter !== 'all'
              ? [
                  {
                    label: 'Clear Filters',
                    onClick: () => {
                      setSearchQuery('');
                      setStatusFilter('all');
                    }
                  }
                ]
              : []
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onEdit={handleEditCampaign}
              onDelete={handleDeleteCampaign}
              onToggleStatus={handleToggleCampaignStatus}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <MultiStepCampaignModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditCampaign(null);
            setInitialCampaignDraft(undefined);
          }}
          onSave={handleCreateCampaign}
          mailboxes={mailboxes}
          templates={templates}
          initialCampaign={initialCampaignDraft || editCampaign || undefined}
        />
      )}

      {showDesignerModal && (
        <AICampaignDesignerModal
          isOpen={showDesignerModal}
          onClose={() => setShowDesignerModal(false)}
          onDesigned={(draft) => {
            setInitialCampaignDraft(draft);
            setEditCampaign(null);
            setShowDesignerModal(false);
            setShowCreateModal(true);
          }}
        />
      )}

      {showGuidedLaunch && (
        <GuidedLaunchModal
          isOpen={showGuidedLaunch}
          onClose={() => setShowGuidedLaunch(false)}
          onLaunch={(draft) => {
            setInitialCampaignDraft(draft);
            setEditCampaign(null);
            setShowGuidedLaunch(false);
            setShowCreateModal(true);
          }}
        />
      )}

      {showPlaybookModal && (
        <PlaybookSelectorModal
          isOpen={showPlaybookModal}
          onClose={() => setShowPlaybookModal(false)}
          onSelect={async (playbook: EmailPlaybookMeta) => {
            try {
              setIsLoadingPlaybooks(true);
              const { campaignDraft } = await emailService.buildPlaybookDraft(playbook.id);
              setInitialCampaignDraft(campaignDraft);
              setEditCampaign(null);
              setShowPlaybookModal(false);
              setShowCreateModal(true);
            } catch (error) {
              console.error('Error building playbook draft:', error);
              toast.error('Failed to build campaign from playbook');
            } finally {
              setIsLoadingPlaybooks(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default ModernEmailCampaigns;
